import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { approveMcpAuthorize } from '@/src/infrastructure/lib/api';

/**
 * Consent screen for the InviteCloud AI MCP desktop extension.
 *
 * The bridge opens this page (via the backend's /ai/mcp/oauth/authorize
 * redirect) in the user's default browser and is listening on a local
 * loopback port for the callback — see docs/mcp/MCP_INTEGRATION_GUIDE.md.
 * This page never sees or handles the MCP session itself; it only asks the
 * already-logged-in user to approve, then hands the browser back to the
 * loopback redirect the backend computed.
 */
export function McpAuthorizePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, hydrated } = useAuth();
  const [status, setStatus] = useState<'idle' | 'approving' | 'error'>('idle');
  const [error, setError] = useState('');

  const redirectUri = searchParams.get('redirect_uri') || '';
  const codeChallenge = searchParams.get('code_challenge') || '';
  const codeChallengeMethod = searchParams.get('code_challenge_method') || '';
  const state = searchParams.get('state') || undefined;
  const isLoopback = /^https?:\/\/(127\.0\.0\.1|\[::1\]|localhost)(?::\d+)?(\/(?:oauth\/)?callback)?\/?$/.test(redirectUri);
  const isHttpsRemote = /^https:\/\/[a-zA-Z0-9.-]+(?::\d+)?(?:\/.*)?$/.test(redirectUri);
  const isTrustedRedirect = isLoopback || isHttpsRemote;

  useEffect(() => {
    if (!hydrated) return;
    if (!user) {
      sessionStorage.setItem('mcp_authorize_return_to', `${location.pathname}${location.search}`);
      navigate('/login', { replace: true });
    }
  }, [hydrated, user, navigate]);

  if (!hydrated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!redirectUri || !codeChallenge || codeChallengeMethod !== 'S256' || !isTrustedRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-4">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="mx-auto text-red-500" size={32} />
          <h1 className="text-lg font-semibold">Invalid authorization request</h1>
          <p className="text-sm text-muted-foreground">
            This link is missing required parameters or isn't from a trusted local application.
            Please restart the connection from the InviteCloud AI extension.
          </p>
        </div>
      </div>
    );
  }

  async function handleApprove() {
    setStatus('approving');
    setError('');
    try {
      const { redirectTo } = await approveMcpAuthorize({
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
        state,
      });
      window.location.href = redirectTo;
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : 'Failed to approve. Please try again.');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="text-primary" size={22} />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Connect InviteCloud AI</h1>
            <p className="text-sm text-muted-foreground">Signed in as {user.email}</p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          A locally-running app on this computer (Claude Desktop, or another MCP client) is asking
          to access your InviteCloud account to extract patrika details, recommend templates, and
          create, update, and publish invitations on your behalf.
        </p>

        {status === 'error' && (
          <p className="text-sm text-red-600" role="alert">{error}</p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleApprove}
            disabled={status === 'approving'}
            className="flex-1 rounded-lg bg-primary text-white py-2.5 text-sm font-medium disabled:opacity-60"
          >
            {status === 'approving' ? 'Approving…' : 'Approve access'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard', { replace: true })}
            disabled={status === 'approving'}
            className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
